import type { SuspensionNotice } from '../mission-specialist/suspensions/useMySuspensions'

import { useMutation } from '@tanstack/react-query'

import { AnnotationTool, SubClassificationToolTypes } from '@sparkai/shared-types'
import { Nullable } from '@sparkai/shared-types'

import { apiClient } from '../apiClient'

type ResolutionV1Request = {
  response: string
  task_id: string
  annotations: {
    type: AnnotationTool
    label: string
    vertices: { x: number; y: number }[]
    original_annotation_id?: string
    image_id: string
    engagement_level: boolean
  }[]
}

type v1ResolutionImage = {
  image_id: string
  location: string
  filename: string
}

type v1ResolutionAnnotation = {
  type: AnnotationTool
  label: string
  vertices: { x: number; y: number }[]
  image_idx: number
  annotation_id: string
  original_annotation_id?: string
}

type ResolutionV1Response = {
  resolution_id: string
  feedback: {
    program: {
      id: string
      name: string
      display_name: string
      label_set: string[]
    }
    images: v1ResolutionImage[]
    originalAnnotations: v1ResolutionAnnotation[] | null
    resolution: {
      response: string
      resolution_id: string
      task_id: string
      engagement_id: string
      answers: string[]
      annotation_type: string
      images: v1ResolutionImage[]
      annotations: v1ResolutionAnnotation[]
      subrequests?: {
        answers: string[]
        question: string[]
      }[]
    }
    groundTruth: {
      response: string
      resolution_id: string
      task_id?: string
      engagement_id?: string
      answers: string[]
      annotation_type: string
      images: v1ResolutionImage[]
      annotations?: v1ResolutionAnnotation[] | null
      subrequests?: {
        answers: string[]
        question: string[]
      }[]
    }
  }
}

type ResolutionRequest = {
  taskId: string
  response: string
  annotations: {
    featureId: string
    featurePredictionId?: string
    metadata: {
      class: string
      tool: AnnotationTool
      vertices: { x: number; y: number }[]
    }
    subClassifications: { featureId: string; type: SubClassificationToolTypes; values: string[] }
  }[]
  isEscalated?: boolean
  escalationReason?: string | null
}

type Vertex = {
  x: number
  y: number
}

type Feature = {
  featureId: string
  programId: string
  metadata: {
    name?: string
    description?: string
    isRequired?: boolean
    options?: { value: string; default: boolean; exampleMediaIds: string[] }[]
    isDropdown?: boolean
    color?: string
    minHeight?: number
    minWidth?: number
    maxOccurrences?: number
    minOccurrences?: number
  }
  markDelete: boolean
  tool: AnnotationTool
}

type ResolutionResponse = {
  resolutionId: string
  activeSuspension: SuspensionNotice | null
}

export type LegacyResolutionResponse = ResolutionResponse & {
  feedback: Nullable<{
    score: number
    media: {
      mediaId: string
      location: string
      filename: string
      metadata: unknown
      mimeType: string
    }[]
    predictions: {
      id: string
      featureId: string
      engagementId: string
      mediaId: string
      data: {
        class?: string
        vertices?: Vertex[]
      }
      feature: Feature
    }[]
    groundTruth: {
      id: string
      resolutionId: string
      featureId: string
      predictionId: string | null
      taskId: string | null
      engagementId: string
      mediaId: string
      data: {
        class?: string
        engagementLevel?: true
        imageIdx?: null
        vertices?: Vertex[]
        mediaId?: string
        type?: string
        values?: string[]
      }
      feature: Feature
    }[]
    resolution: {
      id: string
      resolutionId: string
      featureId: string
      predictionId: string | null
      taskId: string
      engagementId: string
      mediaId: string
      data: {
        class?: string
        engagementLevel?: true
        imageIdx?: null
        vertices?: Vertex[]
        mediaId?: string
        type?: string
        values?: string[]
      }
      feature: Feature
    }[]
  }>
}

export function useResolutionV1() {
  return useMutation({
    mutationFn: async (value: ResolutionV1Request) => {
      const { data } = await apiClient.post<ResolutionV1Response>('/int/task/resolution', value)
      return data
    },
  })
}

export function useResolution() {
  return useMutation({
    mutationFn: async (value: ResolutionRequest) => {
      const { taskId, ...props } = value
      const { data } = await apiClient.post<ResolutionResponse>(`/int/v3/tasks/${taskId}/resolution`, props)
      return data
    },
  })
}
